import { SEARCH_DEBOUNCE_TIME } from 'config/constants';
import useDebounce from 'hooks/helpers/UseDebounce';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { StoreServiceFiltersType } from 'types/store';

export const EMPTY_SERVICE_FILTERS: StoreServiceFiltersType = {
    acceptGiftCoupon: false,
    hasLoyaltyProgram: false,
    isPickupPoint: false,
};

const SERVICE_FILTER_KEYS: (keyof StoreServiceFiltersType)[] = [
    'acceptGiftCoupon',
    'hasLoyaltyProgram',
    'isPickupPoint',
];

const getQueryValue = (value: string | string[] | undefined): string =>
    Array.isArray(value) ? value[0] ?? '' : value ?? '';

const getServiceFiltersFromQuery = (value: string | string[] | undefined): StoreServiceFiltersType => {
    const selectedServices = new Set(getQueryValue(value).split(','));

    return {
        acceptGiftCoupon: selectedServices.has('acceptGiftCoupon'),
        hasLoyaltyProgram: selectedServices.has('hasLoyaltyProgram'),
        isPickupPoint: selectedServices.has('isPickupPoint'),
    };
};

export const useStoreFilters = (onFiltersChange: () => void) => {
    const router = useRouter();
    const [searchValue, setSearchValue] = useState(() => getQueryValue(router.query.search));
    const [selectedRegion, setSelectedRegion] = useState(() => getQueryValue(router.query.region));
    const [selectedDistrict, setSelectedDistrict] = useState(() => getQueryValue(router.query.district));
    const [serviceFilters, setServiceFilters] = useState<StoreServiceFiltersType>(() =>
        getServiceFiltersFromQuery(router.query.services),
    );
    const [shouldLoadTerritorialUnits, setShouldLoadTerritorialUnits] = useState(
        () => getQueryValue(router.query.region) !== '' || getQueryValue(router.query.district) !== '',
    );
    const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE_TIME);

    const replaceFiltersInUrl = useCallback(
        (
            nextSearchValue: string,
            nextRegion: string,
            nextDistrict: string,
            nextServiceFilters: StoreServiceFiltersType,
        ) => {
            const query = { ...router.query };
            delete query.search;
            delete query.region;
            delete query.district;
            delete query.services;

            if (nextSearchValue !== '') {
                query.search = nextSearchValue;
            }
            if (nextRegion !== '') {
                query.region = nextRegion;
            }
            if (nextDistrict !== '') {
                query.district = nextDistrict;
            }

            const selectedServices = SERVICE_FILTER_KEYS.filter((service) => nextServiceFilters[service]);
            if (selectedServices.length > 0) {
                query.services = selectedServices.join(',');
            }

            const localizedPathname = router.asPath.split('?')[0];
            void router.replace(
                { pathname: router.pathname, query },
                { pathname: localizedPathname, query },
                { scroll: false, shallow: true },
            );
        },
        [router],
    );
    const changeRegion = useCallback(
        (region: string) => {
            onFiltersChange();
            setSearchValue('');
            setSelectedRegion(region);
            setSelectedDistrict('');
            replaceFiltersInUrl('', region, '', serviceFilters);
        },
        [onFiltersChange, replaceFiltersInUrl, serviceFilters],
    );
    const changeDistrict = useCallback(
        (district: string) => {
            onFiltersChange();
            setSearchValue('');
            setSelectedDistrict(district);
            replaceFiltersInUrl('', selectedRegion, district, serviceFilters);
        },
        [onFiltersChange, replaceFiltersInUrl, selectedRegion, serviceFilters],
    );
    const changeSearch = useCallback(
        (value: string) => {
            onFiltersChange();
            setSearchValue(value);
            setSelectedRegion('');
            setSelectedDistrict('');
            replaceFiltersInUrl(value, '', '', serviceFilters);
        },
        [onFiltersChange, replaceFiltersInUrl, serviceFilters],
    );
    const changeServiceFilter = useCallback(
        (service: keyof StoreServiceFiltersType, isChecked: boolean) => {
            const nextServiceFilters = { ...serviceFilters, [service]: isChecked };
            onFiltersChange();
            setServiceFilters(nextServiceFilters);
            replaceFiltersInUrl(searchValue, selectedRegion, selectedDistrict, nextServiceFilters);
        },
        [onFiltersChange, replaceFiltersInUrl, searchValue, selectedDistrict, selectedRegion, serviceFilters],
    );
    const resetFilters = useCallback(() => {
        onFiltersChange();
        setSearchValue('');
        setSelectedRegion('');
        setSelectedDistrict('');
        setServiceFilters(EMPTY_SERVICE_FILTERS);
        replaceFiltersInUrl('', '', '', EMPTY_SERVICE_FILTERS);
    }, [onFiltersChange, replaceFiltersInUrl]);
    const loadTerritorialUnits = useCallback(() => setShouldLoadTerritorialUnits(true), []);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        setSearchValue(getQueryValue(router.query.search));
        setSelectedRegion(getQueryValue(router.query.region));
        setSelectedDistrict(getQueryValue(router.query.district));
        setServiceFilters(getServiceFiltersFromQuery(router.query.services));
        if (router.query.region !== undefined || router.query.district !== undefined) {
            setShouldLoadTerritorialUnits(true);
        }
    }, [router.isReady, router.query.district, router.query.region, router.query.search, router.query.services]);

    return {
        changeDistrict,
        changeRegion,
        changeSearch,
        changeServiceFilter,
        debouncedSearchValue,
        loadTerritorialUnits,
        resetFilters,
        searchValue,
        selectedDistrict,
        selectedRegion,
        serviceFilters,
        shouldLoadTerritorialUnits,
    };
};
